import { useTranslation } from 'react-i18next'

import { DatePicker } from '@/components/ui/DatePicker'
import { useSetTrancheDate } from '@/features/cases/api/useCase'
import { refusalMessage } from '@/features/cases/lib/refusal'
import { formatNumber } from '@/lib/format'
import type { ReceivableTranche } from '@/types/domain'

/** The expected receivable series (FR-32, FR-33). Its own block rather than a
 *  fact in a grid, because each row carries an EDITABLE date: AC 3 puts the
 *  expected date in Operations' hands once delivery is known, and the 50-50
 *  split's second tranche is created undated on purpose waiting for exactly
 *  that.
 *
 *  Rendered only when there is a series. A case registered before its rate was
 *  agreed has none, and an empty table would be a heading over nothing.
 *
 *  ── Why it is a table and not a wrapping row ────────────────────────────────
 *
 *  It was `flex flex-wrap` with the picker pushed right by `ml-auto`, which put
 *  the AMOUNTS at a different x on every row — «Транш 1 12 000.00» and «Транш 2
 *  9 000.00» started their figures wherever the label before them happened to
 *  end — and left 900px of nothing between a label and its number. A column of
 *  money that does not line up is the one thing DESIGN.md's rule about numbers
 *  forbids, and it is worse here than anywhere: these figures are a SPLIT of
 *  one rate, and comparing them is the whole reason the series is on screen.
 *
 *  So it is the same `<table>` the cost lines above it are — the same header
 *  eyebrow, the same 18px outer gutter, the same hairline rules, the same
 *  served-total footer. Two tables in one card that agree column for column
 *  read as one document; a table over a bulleted list reads as two.
 *
 *  ── The share, and why this client is allowed to compute it ─────────────────
 *
 *  «50 / 50» is the trade's own words for what this series is, and it is the
 *  fourth column so that the table says the thing the desk actually says. It is
 *  a ratio of two figures the server already sent, in ONE currency — the same
 *  licence `ExpenseTable.groupFigures` works under, and guarded the same way: a
 *  series booked in two currencies has no share to state and prints nothing
 *  rather than a number nobody can defend (AD-9).
 */
export function TrancheStrip({
  caseId,
  tranches,
}: {
  caseId: string
  tranches: ReceivableTranche[]
}) {
  const { t } = useTranslation()
  const setTrancheDate = useSetTrancheDate(caseId)

  if (tranches.length === 0) return null

  const currency = tranches[0]?.amount.currency
  const uniform = tranches.every((tranche) => tranche.amount.currency === currency)
  const total = tranches.reduce((sum, tranche) => sum + Number(tranche.amount.amount), 0)
  /** The tranche's share of the series, or `undefined` when there is nothing to
   *  take a share OF — a series summing to zero, or one booked in two
   *  currencies. */
  const share = (amount: string): number | undefined =>
    uniform && total > 0 ? (Number(amount) / total) * 100 : undefined

  return (
    /* The heavier rule, and it is the only one in the card's tail. Above it are
       the bands of COMPUTED positions — balances and profit — and below it the
       two of RECORD: what is expected and what has arrived. Hairlines inside
       each group, a line between them, which is the whole of the rhythm this
       tail used to lack. */
    <section className="border-line border-t pt-5">
      <h3 className="asa-eyebrow px-[18px]">{t('cases.money.tranchesTitle')}</h3>

      <div className="asa-scroll mt-3 overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-line border-b">
              <th scope="col" className="asa-eyebrow h-9 px-[18px]">
                {t('cases.money.trancheColumns.tranche')}
              </th>
              <th scope="col" className="asa-eyebrow h-9 px-3 text-right">
                {t('cases.money.trancheColumns.share')}
              </th>
              <th scope="col" className="asa-eyebrow h-9 px-3 text-right">
                {t('cases.money.trancheColumns.amount')}
              </th>
              <th scope="col" className="asa-eyebrow h-9 px-[18px] text-right">
                {t('cases.money.trancheDue')}
              </th>
            </tr>
          </thead>

          <tbody>
            {tranches.map((tranche) => {
              const percent = share(tranche.amount.amount)
              return (
                <tr
                  key={tranche.id}
                  className="border-line-hair border-b text-[13px] last:border-b-0"
                >
                  <td className="text-text-heading px-[18px] py-2.5 align-middle whitespace-nowrap">
                    {t('cases.money.trancheOfSeries', {
                      n: tranche.sequence,
                      total: tranches.length,
                    })}
                  </td>
                  <td className="asa-num text-text-muted px-3 py-2.5 text-right align-middle text-[12.5px]">
                    {percent === undefined ? '—' : `${formatNumber(String(percent), 0)}%`}
                  </td>
                  <td className="asa-num text-text-body px-3 py-2.5 text-right align-middle">
                    {formatNumber(tranche.amount.amount, 2)}
                    <span className="text-text-muted ml-1 text-[11.5px] font-light">
                      {tranche.amount.currency}
                    </span>
                  </td>
                  {/* The date, editable in place. PATCH replaces this one field
                      and the server has no method to recompute the amount, so
                      there is nothing else on the row to submit — which is why
                      this is a picker rather than a sheet.

                      Clearing it is legal and means "no agreed date yet", the
                      state the second tranche starts in; an empty value sends
                      null rather than omitting the key.

                      Named by its TRANCHE for a screen reader: the column
                      heading says «Ожидается» once, and a series read out as
                      "Expected, Expected" is a series nobody can act on. */}
                  <td className="px-[18px] py-2 align-middle">
                    <DatePicker
                      className="ml-auto w-[11rem]"
                      value={tranche.expectedOn ?? ''}
                      disabled={setTrancheDate.isPending}
                      aria-label={t('cases.money.trancheDueNamed', { n: tranche.sequence })}
                      onChange={(expectedOn) =>
                        setTrancheDate.mutate({
                          trancheId: tranche.id,
                          expectedOn: expectedOn === '' ? null : expectedOn,
                        })
                      }
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>

          {/* The series' own total, and the reason it is here rather than left
              to the reader: this table sits under «Дебиторка», whose figure it
              is supposed to add up to. A series that does not add up is a
              series somebody has to be able to SEE not adding up.

              Absent across currencies, for `ExpenseTable`'s reason: a sum over
              two of them is the one arithmetic this client must never do. */}
          {tranches.length > 1 && uniform ? (
            <tfoot>
              <tr className="border-line border-t">
                <td colSpan={2} className="text-text-muted px-[18px] py-2.5 text-[12.5px]">
                  {t('cases.money.totals')}
                </td>
                <td className="asa-num text-text-heading px-3 py-2.5 text-right text-[13px] font-semibold">
                  {formatNumber(String(total), 2)}
                  <span className="text-text-muted ml-1 text-[11.5px] font-normal">
                    {currency}
                  </span>
                </td>
                <td className="px-[18px]" />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      {/* A refused date change says so here rather than nowhere: there is
          no global mutation error handler, so an unread `.error` is a
          silent refusal. */}
      {setTrancheDate.error ? (
        <p role="alert" className="text-negative px-[18px] pt-3 pb-4 text-[12px] leading-snug">
          {refusalMessage(setTrancheDate.error, t)}
        </p>
      ) : null}
    </section>
  )
}
